import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MessageList } from "./MessageList.js";
import { ToolCallPanel } from "./ToolCallPanel.js";
import { ApprovalModal } from "./ApprovalModal.js";
import type { Message, StreamEvent, ConversationSummary } from "../api/client.js";
import {
  createConversation,
  sendMessageStreaming,
  listAgents,
  listConversations,
  getMessages,
  renameConversation,
  deleteConversation,
  type Agent,
} from "../api/client.js";

interface ToolCallState {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  status?: string;
  output?: unknown;
  durationMs?: number;
}

interface ApprovalRequest {
  toolCallId: string;
  toolName: string;
  prompt: string;
}

export function ChatPage() {
  const { conversationId: urlConvId } = useParams();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [conversationId, setConversationId] = useState(urlConvId ?? "");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [toolCalls, setToolCalls] = useState<ToolCallState[]>([]);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [history, setHistory] = useState<ConversationSummary[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load agents
  useEffect(() => {
    listAgents()
      .then((res) => {
        setAgents(res.agents);
        if (res.agents.length > 0 && !selectedAgent) {
          setSelectedAgent(res.agents[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Load conversation history
  const refreshHistory = useCallback(() => {
    listConversations(searchQuery || undefined)
      .then((res) => setHistory(res.conversations))
      .catch(() => {});
  }, [searchQuery]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  // Sync with URL params when navigating
  useEffect(() => {
    if (urlConvId && urlConvId !== conversationId) {
      setConversationId(urlConvId);
      setMessages([]);
      setStreamBuffer("");
      setToolCalls([]);
      getMessages(urlConvId)
        .then((res) => {
          setMessages(res.messages.filter((m) => m.role === "user" || m.role === "assistant"));
        })
        .catch((err) => {
          // Conversation no longer exists — redirect to fresh chat
          if (err?.status === 404) {
            setConversationId("");
            navigate("/chat", { replace: true });
          }
        });
    }
  }, [urlConvId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamBuffer]);

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case "text":
        setStreamBuffer((prev) => prev + event.data.token);
        break;
      case "tool_call_start":
        setToolCalls((prev) => [
          ...prev,
          {
            toolCallId: event.data.toolCallId,
            toolName: event.data.toolName,
            args: event.data.args,
          },
        ]);
        break;
      case "tool_call_result":
        setToolCalls((prev) =>
          prev.map((tc) =>
            tc.toolCallId === event.data.toolCallId
              ? { ...tc, status: event.data.status, output: event.data.output, durationMs: event.data.durationMs }
              : tc,
          ),
        );
        break;
      case "approval_required":
        setApproval({
          toolCallId: event.data.toolCallId,
          toolName: event.data.toolName,
          prompt: event.data.prompt,
        });
        break;
      case "error":
        setStreamBuffer((prev) => {
          const errorText = prev || "Something went wrong — please try again.";
          setMessages((msgs) => [...msgs, { role: "assistant", content: errorText }]);
          return "";
        });
        setStreaming(false);
        break;
      case "done":
        setStreamBuffer((prev) => {
          if (prev) {
            setMessages((msgs) => [...msgs, { role: "assistant", content: prev }]);
          }
          return "";
        });
        setStreaming(false);
        break;
    }
  }, []);

  async function handleSend() {
    const text = input.trim();
    if (!text || streaming) return;

    setInput("");
    setToolCalls([]);

    // Add user message
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    // Create conversation if needed
    let convId = conversationId;
    if (!convId && selectedAgent) {
      try {
        const conv = await createConversation(selectedAgent);
        convId = conv.id;
        setConversationId(convId);
        navigate(`/chat/${convId}`, { replace: true });
        refreshHistory();
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Failed to create conversation." },
        ]);
        return;
      }
    }

    // Send message with real-time streaming (single SSE POST)
    setStreaming(true);
    sendMessageStreaming(
      convId,
      text,
      (event) => {
        handleStreamEvent(event);
        if (event.type === "done") {
          refreshHistory();
        }
      },
      (err) => {
        // If the conversation no longer exists (404), reset and let the user retry
        if (err?.message?.includes("404")) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: "This conversation no longer exists. Starting a fresh chat — please resend your message." },
          ]);
          setConversationId("");
          navigate("/chat", { replace: true });
        } else {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: "Failed to send message." },
          ]);
        }
        setStreaming(false);
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleNewChat() {
    setConversationId("");
    setMessages([]);
    setStreamBuffer("");
    setToolCalls([]);
    navigate("/chat");
  }

  function handleSelectConversation(id: string) {
    if (id === conversationId) return;
    navigate(`/chat/${id}`);
  }

  function handleStartRename(conv: ConversationSummary) {
    setEditingTitle(conv.id);
    setEditTitleValue(conv.title ?? conv.preview ?? "");
  }

  async function handleRenameSubmit(id: string) {
    const title = editTitleValue.trim();
    if (title) {
      await renameConversation(id, title).catch(() => {});
      refreshHistory();
    }
    setEditingTitle(null);
  }

  async function handleDeleteConversation(id: string) {
    await deleteConversation(id).catch(() => {});
    setDeleteConfirm(null);
    if (id === conversationId) {
      setConversationId("");
      setMessages([]);
      navigate("/chat");
    }
    refreshHistory();
  }

  return (
    <div className="flex h-full">
      {/* Conversation history sidebar */}
      <div className="flex w-64 shrink-0 flex-col border-r border-ink-100 bg-surface-1">
        <div className="flex h-14 items-center justify-between border-b border-ink-100 px-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-500">History</h2>
          <button
            onClick={handleNewChat}
            className="rounded-lg bg-ink-950 px-2.5 py-1 text-xs font-medium text-white hover:bg-ink-800 transition-colors"
          >
            + New
          </button>
        </div>
        {/* Search */}
        <div className="px-2 py-2 border-b border-ink-100">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full rounded-md border border-ink-200 bg-white px-2.5 py-1.5 text-xs text-ink-700 placeholder:text-ink-400 focus:border-ink-400 focus:outline-none"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {history.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-ink-400">
              {searchQuery ? "No matches found" : "No conversations yet"}
            </p>
          ) : (
            history.map((conv) => {
              const agent = agents.find((a) => a.id === conv.agentId);
              const isActive = conv.id === conversationId;
              const displayTitle = conv.title ?? conv.preview ?? "New conversation";
              const tokenCount = conv.totalInputTokens + conv.totalOutputTokens;
              return (
                <div key={conv.id} className="group relative">
                  {/* Delete confirmation */}
                  {deleteConfirm === conv.id && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-red-50 border border-red-200 px-2">
                      <span className="text-xs text-red-700 mr-2">Delete?</span>
                      <button
                        onClick={() => handleDeleteConversation(conv.id)}
                        className="rounded bg-red-600 px-2 py-0.5 text-xs text-white hover:bg-red-700 mr-1"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="rounded bg-ink-200 px-2 py-0.5 text-xs text-ink-700 hover:bg-ink-300"
                      >
                        No
                      </button>
                    </div>
                  )}
                  {/* Inline rename */}
                  {editingTitle === conv.id ? (
                    <div className="rounded-lg px-3 py-2.5 bg-surface-2">
                      <input
                        autoFocus
                        value={editTitleValue}
                        onChange={(e) => setEditTitleValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameSubmit(conv.id);
                          if (e.key === "Escape") setEditingTitle(null);
                        }}
                        onBlur={() => handleRenameSubmit(conv.id)}
                        className="w-full rounded border border-ink-300 bg-white px-2 py-1 text-sm text-ink-800 focus:outline-none focus:border-ink-500"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSelectConversation(conv.id)}
                      onDoubleClick={() => handleStartRename(conv)}
                      className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                        isActive
                          ? "bg-ink-950 text-white"
                          : "text-ink-700 hover:bg-surface-2"
                      }`}
                    >
                      <p className={`truncate text-sm font-medium ${isActive ? "text-white" : "text-ink-800"}`}>
                        {displayTitle.length > 40 ? displayTitle.slice(0, 40) + "..." : displayTitle}
                      </p>
                      <div className={`mt-0.5 flex items-center justify-between text-xs ${isActive ? "text-white/60" : "text-ink-400"}`}>
                        <span>{agent?.name ?? "Agent"} &middot; {formatRelativeTime(conv.updatedAt)}</span>
                        {tokenCount > 0 && (
                          <span className="tabular-nums">{formatTokens(tokenCount)}</span>
                        )}
                      </div>
                    </button>
                  )}
                  {/* Action buttons (visible on hover) */}
                  {editingTitle !== conv.id && deleteConfirm !== conv.id && (
                    <div className={`absolute right-1 top-1 hidden group-hover:flex gap-0.5 ${isActive ? "" : ""}`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartRename(conv); }}
                        className="rounded p-1 text-ink-400 hover:bg-ink-200 hover:text-ink-600"
                        title="Rename"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(conv.id); }}
                        className="rounded p-1 text-ink-400 hover:bg-red-100 hover:text-red-600"
                        title="Delete"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex flex-1 flex-col">
        {/* Header */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-semibold text-ink-900">Chat</h1>
            {conversationId && (
              <span className="badge-gray font-mono">{conversationId.slice(0, 8)}</span>
            )}
          </div>
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700"
          >
            <option value="">Select agent...</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.provider ? ` (${a.provider})` : ""}
              </option>
            ))}
          </select>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-6 py-6">
            {messages.length === 0 && !streaming ? (
              <EmptyState />
            ) : (
              <>
                <MessageList messages={messages} />

                {/* Active stream */}
                {streamBuffer && (
                  <div className="animate-slide-up mt-4">
                    <MessageList
                      messages={[{ role: "assistant", content: streamBuffer }]}
                    />
                  </div>
                )}

                {/* Tool call panels */}
                {toolCalls.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {toolCalls.map((tc) => (
                      <ToolCallPanel key={tc.toolCallId} {...tc} />
                    ))}
                  </div>
                )}

                {/* Streaming indicator */}
                {streaming && !streamBuffer && (
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-ink-400">
                    <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-ink-400" />
                    <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-ink-400" style={{ animationDelay: "0.2s" }} />
                    <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-ink-400" style={{ animationDelay: "0.4s" }} />
                  </div>
                )}
              </>
            )}
            <div ref={endRef} />
          </div>
        </div>

        {/* Input */}
        <div className="shrink-0 border-t border-ink-100 bg-white px-6 py-4">
          <div className="mx-auto flex max-w-3xl items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Send a message..."
              rows={1}
              className="input max-h-36 min-h-[2.5rem] resize-none"
              style={{ height: `${Math.min(input.split("\n").length * 24 + 16, 144)}px` }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || streaming}
              className="btn-primary shrink-0"
            >
              <SendIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Approval modal */}
        {approval && (
          <ApprovalModal
            toolCallId={approval.toolCallId}
            toolName={approval.toolName}
            prompt={approval.prompt}
            onApprove={() => setApproval(null)}
            onReject={() => setApproval(null)}
          />
        )}
      </div>
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center py-24">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2">
          <svg className="h-6 w-6 text-ink-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 3h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5l-3 3V4a1 1 0 0 1 1-1z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-ink-800">Start a conversation</h2>
        <p className="mt-1 text-xs text-ink-500">Select an agent and send a message</p>
      </div>
    </div>
  );
}

function formatTokens(count: number): string {
  if (count < 1000) return `${count}t`;
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}k`;
  return `${(count / 1_000_000).toFixed(1)}M`;
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2L7 9" />
      <path d="M14 2l-5 12-2-5-5-2 12-5z" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 0 1 1.34-1.34h2.66a1.33 1.33 0 0 1 1.34 1.34V4m2 0v9.33a1.33 1.33 0 0 1-1.34 1.34H4.67a1.33 1.33 0 0 1-1.34-1.34V4" />
    </svg>
  );
}
